import { getAllRoutes } from '@lognai/frontend-core';
import { getDatasetDetailId } from './breadcrumbs';

export interface AiPageContext {
  pluginId: string;
  entityType: string;
  entityId: string;
  url: string;
}

/**
 * Is this path segment a registered STATIC route rather than an entity id?
 *
 * Compares the path against the route registry — the same source the
 * breadcrumb clickability check uses — so any sibling page a plugin registers
 * is excluded automatically.
 */
export const isRegisteredStaticSegment = (pathname: string, segment: string): boolean => {
  const actual = pathname.split('/').filter(Boolean);
  const index = actual.indexOf(segment);
  if (index === -1) return false;

  return getAllRoutes().some((route) => {
    const pattern = route.path.split('/').filter(Boolean);
    if (pattern.length <= index) return false;
    if (pattern[index] !== segment) return false;
    return pattern.slice(0, index).every((part, i) => part.startsWith(':') || part === actual[i]);
  });
};

/**
 * Derive the record the user is currently looking at. This context is sent to
 * LognAI so phrases such as "this request" resolve to the visible record and
 * users are never asked to repeat an id that is already present in the URL.
 */
export const deriveAiPageContext = (pathname: string, search = ''): AiPageContext | undefined => {
  const datasetId = getDatasetDetailId(pathname, search);
  if (datasetId) return {
    pluginId: 'analytics', entityType: 'dataset', entityId: datasetId,
    url: pathname + search,
  };
  const patterns: Array<[RegExp, { pluginId: string; entityType: string }]> = [
    [/^\/analytics\/themes\/([^/]+)\/?$/, { pluginId: 'analytics', entityType: 'theme' }],
    [/^\/analytics\/dashboards\/([^/]+)\/?$/, { pluginId: 'analytics', entityType: 'dashboard' }],
    [
      /^\/invoices\/(?:incoming|payables|receivables)\/([^/]+)/,
      { pluginId: 'invoices', entityType: 'invoice' },
    ],
    [/^\/invoices\/payments\/([^/]+)/, { pluginId: 'invoices', entityType: 'payment' }],
    [/^\/invoices\/([^/]+)$/, { pluginId: 'invoices', entityType: 'invoice' }],
    [/^\/freight-audit\/cases\/([^/]+)/, { pluginId: 'freight-audit', entityType: 'case' }],
    [
      /^\/freight-audit\/invoices\/([^/]+)/,
      { pluginId: 'freight-audit', entityType: 'invoiceFamily' },
    ],
    [/^\/orders\/purchase\/([^/]+)/, { pluginId: 'orders', entityType: 'purchaseOrder' }],
    [/^\/orders\/sales\/([^/]+)/, { pluginId: 'orders', entityType: 'salesOrder' }],
    [/^\/orders\/backorders\/([^/]+)/, { pluginId: 'orders', entityType: 'backorder' }],
    [/^\/shipments\/([^/]+)/, { pluginId: 'shipments', entityType: 'shipment' }],
    [/^\/inventory\/items\/([^/]+)/, { pluginId: 'inventory', entityType: 'inventoryItem' }],
    [/^\/inventory\/lots\/([^/]+)/, { pluginId: 'inventory', entityType: 'lot' }],
    [/^\/inventory\/serials\/([^/]+)/, { pluginId: 'inventory', entityType: 'serial' }],
    [/^\/products\/([^/]+)/, { pluginId: 'products', entityType: 'product' }],
    [/^\/warehouse\/warehouses\/([^/]+)/, { pluginId: 'warehouse', entityType: 'warehouse' }],
    [/^\/landed-cost\/orders\/([^/]+)/, { pluginId: 'orders', entityType: 'salesOrder' }],
    // FleetMon's current mission route and its compatibility alias must resolve
    // to the same assignment entity. Keep the more-specific alias first.
    [
      /^\/fleetmon\/dispatch\/assignments\/([^/]+)$/,
      { pluginId: 'fleetmon', entityType: 'fleetmonAssignment' },
    ],
    [/^\/fleetmon\/dispatch\/([^/]+)$/, { pluginId: 'fleetmon', entityType: 'fleetmonAssignment' }],
    [/^\/fleetmon\/requests\/([^/]+)$/, { pluginId: 'fleetmon', entityType: 'fleetmonRequest' }],
    [
      /^\/fleetmon\/fleet\/vehicles\/([^/]+)$/,
      { pluginId: 'fleetmon', entityType: 'fleetmonVehicle' },
    ],
    [/^\/fleetmon\/drivers\/([^/]+)$/, { pluginId: 'fleetmon', entityType: 'fleetmonDriver' }],
    [
      /^\/fleetmon\/fleet\/permits\/([^/]+)$/,
      { pluginId: 'fleetmon', entityType: 'fleetmonPermit' },
    ],
    [
      /^\/fleetmon\/fleet\/fuel\/transactions\/([^/]+)$/,
      { pluginId: 'fleetmon', entityType: 'fleetmonFuelTransaction' },
    ],
    [
      /^\/fleetmon\/fleet\/ifta\/reports\/([^/]+)$/,
      { pluginId: 'fleetmon', entityType: 'fleetmonIFTA' },
    ],
    // The workflow builder: the chat edits the workflow that is open.
    [/^\/workflow-builder\/([^/]+)\/?$/, { pluginId: 'workflows', entityType: 'definition' }],
    [/^\/companies\/([^/]+)/, { pluginId: 'admin', entityType: 'company' }],
    [/^\/admin\/users\/([^/]+)\/(?:roles|access)$/, { pluginId: 'admin', entityType: 'user' }],
  ];

  for (const [pattern, metadata] of patterns) {
    const match = pathname.match(pattern);
    if (
      match?.[1] &&
      ![
        'new',
        'create',
        'incoming',
        'payables',
        'receivables',
        'payments',
        'exceptions',
        'settings',
        'portal',
      ].includes(match[1]) &&
      !isRegisteredStaticSegment(pathname, match[1])
    ) {
      return { ...metadata, entityId: match[1], url: pathname };
    }
  }

  return undefined;
};
